import { Controller } from "@hotwired/stimulus"
import { responseIndicatesSessionExpired, responseRedirectedToLogin } from "../lib/session_expired.mjs"

// Lazy claim/bill panels. Selection is shared with the optional sliding tab bar
// via aria-current and a bubbling ajax-tabs:change event.
export default class extends Controller {
  static targets = ["tab", "content", "tabContent"]
  static values = { updateTabUrl: String }

  initialize() {
    this.boundHandlePopstate = this.handlePopstate.bind(this)
  }

  connect() {
    window.addEventListener("popstate", this.boundHandlePopstate)
    this.hideAllLoading()
    this.loadedUrl = this.hasContentTarget && this.contentTarget.dataset.ajaxTabsLoadedUrl || window.location.href
    this.loadedTab = this.tabForUrl(this.loadedUrl)
    const activeTab = this.tabForUrl(window.location.href) || this.tabTargets.find(tab => tab.getAttribute("aria-current") === "page")
    this.loadedTab ||= activeTab
    if (activeTab) {
      this.setActiveTab(activeTab, false)
      // A cached page can have been captured while its next panel was loading.
      if (this.loadedTab && activeTab !== this.loadedTab) this.loadTabContent(activeTab)
    }
    this.rememberLoadedUrl()
  }

  navigate(event) {
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || (event.button ?? 0) !== 0) return
    const tab = event.currentTarget
    if (tab.target && tab.target !== "_self") return
    event.preventDefault()

    if (tab.href !== window.location.href) window.history.pushState(null, "", tab.href)
    this.setActiveTab(tab)
    this.loadTabContent(tab)
  }

  setActiveTab(activeTab, animate = true) {
    this.tabTargets.forEach(tab => {
      const selected = tab === activeTab
      if (selected) tab.setAttribute("aria-current", "page")
      else tab.removeAttribute("aria-current")
      tab.classList.toggle("mb-[-2px]", selected)
      const content = this.findTabContentForTab(tab)
      for (const name of ["border-b-2", "border-purple", "mb-[-2px]"]) content?.classList.toggle(name, selected)
    })
    this.dispatch("change", { target: activeTab, detail: { animate } })
  }

  tabForUrl(url) {
    const target = new URL(url, window.location.href)
    return this.tabTargets.find(tab => {
      const candidate = new URL(tab.href)
      return candidate.pathname === target.pathname && (!candidate.search || candidate.search === target.search)
    })
  }

  async loadTabContent(tab) {
    this.request?.abort()
    const request = new AbortController()
    this.request = request
    this.hideAllLoading()
    tab.setAttribute("aria-busy", "true")
    this.findTabContentForTab(tab)?.classList.add("tab-loading")
    if (this.hasContentTarget) this.contentTarget.setAttribute("aria-busy", "true")

    try {
      const formData = new FormData()
      formData.append("tab", tab.dataset.tabName)
      const response = await fetch(this.updateTabUrlValue, {
        method: "POST",
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: formData,
        signal: request.signal
      })
      if (request.signal.aborted) return

      // The global fetch interceptor shows the standard login toast. Keep the
      // loaded panel and its URL rather than inserting the followed login HTML.
      if (responseIndicatesSessionExpired(response.status, name => response.headers.get(name)) || responseRedirectedToLogin(response)) {
        this.restoreLoadedTab()
        return
      }
      if (!response.ok || !response.headers.get("Content-Type")?.includes("text/vnd.turbo-stream.html")) {
        throw new Error("Tab response was not a successful Turbo Stream")
      }
      const message = await response.text()
      if (request.signal.aborted) return
      await this.renderTabContent(message, request)
      if (request.signal.aborted || !this.element.isConnected) return
      this.loadedTab = tab
      this.loadedUrl = window.location.href
      this.rememberLoadedUrl()
    } catch (error) {
      // A slow earlier response must never overwrite a newer selection, even
      // when abort arrives after its headers and while its body is being read.
      if (!request.signal.aborted) window.location.assign(tab.href)
    } finally {
      if (this.request === request) {
        this.request = null
        this.hideAllLoading()
      }
    }
  }

  async renderTabContent(message, request) {
    const streams = []
    const guardRender = event => {
      const render = event.detail.render
      event.detail.render = stream => {
        if (!request.signal.aborted && this.element.isConnected) return render(stream)
      }
      streams.push(event.target)
    }
    // Stream connection dispatches this event synchronously, but the action runs
    // after Turbo's next repaint. Guard that action and await its render promise
    // before marking the panel loaded or letting a snapshot record its new URL.
    document.addEventListener("turbo:before-stream-render", guardRender)
    try {
      Turbo.renderStreamMessage(message)
    } finally {
      document.removeEventListener("turbo:before-stream-render", guardRender)
    }
    await Promise.all(streams.map(stream => stream.render()))
  }

  rememberLoadedUrl() {
    if (this.hasContentTarget) this.contentTarget.dataset.ajaxTabsLoadedUrl = this.loadedUrl
  }

  restoreLoadedTab() {
    if (this.loadedTab) this.setActiveTab(this.loadedTab)
    window.history.replaceState(window.history.state, "", this.loadedUrl)
  }

  hideAllLoading() {
    this.tabTargets.forEach(tab => tab.removeAttribute("aria-busy"))
    this.tabContentTargets.forEach(content => content.classList.remove("tab-loading"))
    if (this.hasContentTarget) this.contentTarget.removeAttribute("aria-busy")
  }

  findTabContentForTab(tab) {
    return tab.querySelector('[data-ajax-tabs-target="tabContent"]')
  }

  handlePopstate(event) {
    // Turbo owns restoration of its own entries, including the initial visit.
    // Our pushState entries have no Turbo state and keep the page mounted.
    this.request?.abort()
    if (event.state?.turbo) return
    const tab = this.tabForUrl(window.location.href)
    if (!tab) return
    this.setActiveTab(tab)
    this.loadTabContent(tab)
  }

  disconnect() {
    window.removeEventListener("popstate", this.boundHandlePopstate)
    this.request?.abort()
    this.hideAllLoading()
  }
}
